import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import {
  CANVASSING_SESSION_CAPTURE_STATUS_OPTIONS,
  CANVASSING_SESSION_PROCESSING_STATUS_OPTIONS,
  CANVASSING_SESSION_SORT_OPTIONS,
  DATE_RANGE_OPTIONS,
  parseCanvassingSessionCaptureStatus,
  parseCanvassingSessionProcessingStatus,
  getCanvassingSessionCustomDateErrors,
  type CanvassingSessionDateRangePreset,
  type CanvassingSessionFilterState,
} from './canvassingSessionFilters';

export type CanvassingSessionFilterBarProps = {
  filters: CanvassingSessionFilterState;
  onChange: (filters: CanvassingSessionFilterState) => void;
  dateRange: CanvassingSessionDateRangePreset;
  onDateRangeChange: (dateRange: CanvassingSessionDateRangePreset) => void;
  customFromDate: string;
  customThroughDate: string;
  onCustomRangeChange: (fromDate: string, throughDate: string) => void;
  calendarTimeZone: string | null;
  canvasserOptions: string[];
  resolveCanvasserLabel: (canvasserId: string) => string;
};

const NONE_VALUE = '';

const selectedCanvasserOptions = (options: string[], selected: string | null): string[] =>
  selected && !options.includes(selected) ? [selected, ...options] : options;

export const CanvassingSessionFilterBar = ({
  filters,
  onChange,
  dateRange,
  onDateRangeChange,
  customFromDate,
  customThroughDate,
  onCustomRangeChange,
  calendarTimeZone,
  canvasserOptions,
  resolveCanvasserLabel,
}: CanvassingSessionFilterBarProps) => {
  const availableCanvassers = selectedCanvasserOptions(canvasserOptions, filters.canvasserId);
  const customDateErrors = getCanvassingSessionCustomDateErrors(
    customFromDate || undefined,
    customThroughDate || undefined,
  );

  const handleDateRangeChange = (event: SelectChangeEvent): void => {
    const nextDateRange = DATE_RANGE_OPTIONS.find(option => option.value === event.target.value)?.value;
    if (nextDateRange) onDateRangeChange(nextDateRange);
  };

  const handleSortChange = (event: SelectChangeEvent): void => {
    const nextSort = CANVASSING_SESSION_SORT_OPTIONS.find(option => option.value === event.target.value)?.value;
    if (nextSort) onChange({ ...filters, sort: nextSort });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="canvassing-session-date-range-label">Date range</InputLabel>
          <Select
            labelId="canvassing-session-date-range-label"
            label="Date range"
            value={dateRange}
            onChange={handleDateRangeChange}
          >
            {DATE_RANGE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {dateRange === 'custom' && (
          <Stack sx={{ flex: '1 1 416px', minWidth: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 2, minWidth: 0 }}>
              <TextField
                id="canvassing-session-from-date"
                type="date"
                label="From date"
                value={customFromDate}
                onChange={event => onCustomRangeChange(event.target.value, customThroughDate)}
                error={customDateErrors.fromDate !== null}
                helperText={customDateErrors.fromDate ?? ' '}
                slotProps={{
                  htmlInput: {
                    'aria-invalid': customDateErrors.fromDate !== null,
                    'aria-describedby':
                      'canvassing-session-from-date-helper-text canvassing-session-calendar-timezone',
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ minWidth: 200, maxWidth: '100%', flex: '1 1 200px' }}
              />
              <TextField
                id="canvassing-session-through-date"
                type="date"
                label="Through date"
                value={customThroughDate}
                onChange={event => onCustomRangeChange(customFromDate, event.target.value)}
                error={customDateErrors.throughDate !== null}
                helperText={customDateErrors.throughDate ?? ' '}
                slotProps={{
                  htmlInput: {
                    'aria-invalid': customDateErrors.throughDate !== null,
                    'aria-describedby':
                      'canvassing-session-through-date-helper-text canvassing-session-calendar-timezone',
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ minWidth: 200, maxWidth: '100%', flex: '1 1 200px' }}
              />
            </Stack>
            <FormHelperText id="canvassing-session-calendar-timezone" sx={{ m: 0, mt: 0.5 }}>
              Dates are inclusive. Calendar timezone: {calendarTimeZone ?? 'not yet available'}.
            </FormHelperText>
          </Stack>
        )}

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }} disabled={availableCanvassers.length === 0}>
          <InputLabel id="canvassing-session-canvasser-label">Canvasser</InputLabel>
          <Select
            labelId="canvassing-session-canvasser-label"
            label="Canvasser"
            value={availableCanvassers.length === 0 ? NONE_VALUE : (filters.canvasserId ?? NONE_VALUE)}
            onChange={(event: SelectChangeEvent) =>
              onChange({ ...filters, canvasserId: event.target.value === NONE_VALUE ? null : event.target.value })
            }
          >
            <MenuItem value={NONE_VALUE}>
              {availableCanvassers.length === 0 ? 'None available' : 'All canvassers'}
            </MenuItem>
            {availableCanvassers.map(canvasserId => (
              <MenuItem key={canvasserId} value={canvasserId}>
                {resolveCanvasserLabel(canvasserId)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="canvassing-session-capture-status-label">Capture status</InputLabel>
          <Select
            labelId="canvassing-session-capture-status-label"
            label="Capture status"
            value={filters.captureStatus ?? NONE_VALUE}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...filters,
                captureStatus:
                  event.target.value === NONE_VALUE ? null : parseCanvassingSessionCaptureStatus(event.target.value),
              })
            }
          >
            <MenuItem value={NONE_VALUE}>All capture statuses</MenuItem>
            {CANVASSING_SESSION_CAPTURE_STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="canvassing-session-processing-status-label">Processing status</InputLabel>
          <Select
            labelId="canvassing-session-processing-status-label"
            label="Processing status"
            value={filters.processingStatus ?? NONE_VALUE}
            onChange={(event: SelectChangeEvent) =>
              onChange({
                ...filters,
                processingStatus:
                  event.target.value === NONE_VALUE ? null : parseCanvassingSessionProcessingStatus(event.target.value),
              })
            }
          >
            <MenuItem value={NONE_VALUE}>All processing statuses</MenuItem>
            {CANVASSING_SESSION_PROCESSING_STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="medium" sx={{ minWidth: 200, flex: '1 0 0' }}>
          <InputLabel id="canvassing-session-sort-label">Sort</InputLabel>
          <Select
            labelId="canvassing-session-sort-label"
            label="Sort"
            value={filters.sort}
            onChange={handleSortChange}
          >
            {CANVASSING_SESSION_SORT_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};
